import React, { FC, ReactNode } from 'react';
import { cn } from '../../utility/cn';
import { getPaginationRange } from './table.utils';

// --- Interfaces ---

interface TableProps {
    children: ReactNode;
    className?: string;
}

interface TableHeadCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
    sortDirection?: 'asc' | 'desc' | null;
    onSort?: () => void;
    className?: string;
    width?: string | number;
}

interface TablePaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    limit: number;
    onLimitChange: (limit: number) => void;
    className?: string;
}

// --- Components ---

export const Table: FC<TableProps> = ({ children, className }) => {
    return (
        <div className="table-responsive">
            <table className={cn("table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4", className)}>
                {children}
            </table>
        </div>
    );
};

export const TableHeader: FC<TableProps> = ({ children, className }) => {
    return (
        <thead>
            <tr className={cn("fw-bolder text-muted", className)}>
                {children}
            </tr>
        </thead>
    );
};

export const TableBody: FC<TableProps> = ({ children, className }) => {
    return <tbody className={className}>{children}</tbody>;
};

export const TableRow: FC<React.HTMLAttributes<HTMLTableRowElement>> = ({ children, className, ...props }) => {
    return (
        <tr className={className} {...props}>
            {children}
        </tr>
    );
};

export const TableHead: FC<TableHeadCellProps> = ({
    children,
    sortDirection,
    onSort,
    className,
    style,
    width,
    ...props
}) => {
    return (
        <th
            className={cn(width ? `min-w-${width}` : '', onSort ? "cursor-pointer" : "", className)}
            style={{ ...style }}
            onClick={onSort}
            {...props}
        >
            {children}
            {sortDirection && (
                <span className="ms-1">
                    {sortDirection === 'asc' ? '▲' : '▼'}
                </span>
            )}
        </th>
    );
};

export const TableCell: FC<React.TdHTMLAttributes<HTMLTableCellElement>> = ({ children, className, ...props }) => {
    return (
        <td className={className} {...props}>
            {children}
        </td>
    );
};

// --- Specific Pagination Component (Metronic Style) ---
export const TablePagination: FC<TablePaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    limit,
    onLimitChange,
}) => {
    if (totalPages <= 1) return null;
    const paginationRange = getPaginationRange(currentPage, totalPages)

    return (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-5 pb-5">
            {/* Limit Selector */}
            <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
                <span className="text-muted fw-bold">Show</span>
                <select
                    className="form-select form-select-sm w-auto"
                    value={limit}
                    onChange={(e) => onLimitChange(Number(e.target.value))}
                >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                </select>
                <span className="text-muted fw-bold">entries</span>
            </div>

            {/* Pagination Controls */}
            <ul className="pagination mb-0">
                {/* Previous */}
                <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <i className="previous"></i>
                    </button>
                </li>

                {/* Page Numbers */}
                {paginationRange.map((page, index) => {
                    if (page === 'dots') {
                        return (
                            <li key={index} className="page-item disabled">
                                <span className="page-link">…</span>
                            </li>
                        );
                    }
                    return (
                        <li key={index} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button
                                type="button"
                                className="page-link"
                                onClick={() => onPageChange(Number(page))}
                            >
                                {page}
                            </button>
                        </li>
                    );
                })}

                {/* Next */}
                <li className={`page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="next"></i>
                    </button>
                </li>
            </ul>
        </div>
    );
};

// --- how to use example

// const users = [
//     { id: 1, name: "Alice Johnson", email: "alice@example.com", status: "Active" },
//     { id: 2, name: "Bob Smith", email: "bob@example.com", status: "Pending" },
//     { id: 3, name: "Charlie Brown", email: "charlie@example.com", status: "Active" },
// ];

// const SimpleUserTable = () => {
//     return (
//         <div className="card shadow-sm">
//             <div className="card-header">
//                 <h3 className="card-title">User Management</h3>
//             </div>

//             <div className="card-body">
//                 {/* 2. The Table Structure */}
//                 <Table>
//                     <TableHeader>
//                         <TableHead width="200px">User</TableHead>
//                         <TableHead>Email</TableHead>
//                         <TableHead>Status</TableHead>
//                         <TableHead className="text-end">Actions</TableHead>
//                     </TableHeader>

//                     <TableBody>
//                         {users.map((user) => (
//                             <TableRow key={user.id}>

//                                 <TableCell className="fw-bold text-gray-800">
//                                     {user.name}
//                                 </TableCell>

//                                 <TableCell className="text-muted">
//                                     {user.email}
//                                 </TableCell>

//                                 <TableCell>
//                                     <span className={`badge ${user.status === 'Active' ? 'badge-light-success' : 'badge-light-warning'}`}>
//                                         {user.status}
//                                     </span>
//                                 </TableCell>

//                                 <TableCell className="text-end">
//                                     <button className="btn btn-sm btn-light btn-active-light-primary">
//                                         Edit
//                                     </button>
//                                 </TableCell>

//                             </TableRow>
//                         ))}
//                     </TableBody>
//                 </Table>
//             </div>
//         </div>
//     );
// };

